import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {saveAs} from 'file-saver';
import {FileDTO} from '../../clm/models/FileDTO';
import {SharedService} from './shared-service';
import {environment} from '../../../../environments/environment';

interface FileDownloadOptions {
  showSnackbar?: boolean;
  successMessage?: string;
  errorMessage?: string;
  successCallback?: () => void;
  errorCallback?: (error: any) => void;
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private fileUploadUrl = environment.baseUrl + '/file';

  constructor(
    private httpClient: HttpClient,
    private sharedService: SharedService
  ) {
  }

  uploadFile(fileDTO: FileDTO): Observable<FileDTO> {
    return this.httpClient.post<FileDTO>(this.fileUploadUrl, fileDTO);
  }

  deleteFile(fileId: number | bigint): Observable<any> {
    return this.httpClient.delete(`${this.fileUploadUrl}/${fileId}`);
  }

  getFile(fileId: bigint | number): Observable<FileDTO> {
    return this.httpClient.get<FileDTO>(`${this.fileUploadUrl}/${fileId}`);
  }

  downloadFile(fileId: string | number, fileName: string | null, options: FileDownloadOptions = {}): void {
    const defaultOptions: FileDownloadOptions = {
      showSnackbar: true,
      successMessage: 'Document downloaded successfully',
      errorMessage: 'Error downloading document - contact an administrator for assistance',
      successCallback: null,
      errorCallback: null
    };

    const mergedOptions = {...defaultOptions, ...options};

    this.getFile(Number(fileId)).pipe(
      map((fileDTO: FileDTO) => {
        const {name, extension, file} = fileDTO;
        const blobData = this.base64toBlob(file, 'application/octet-stream');
        const blob = new Blob([blobData], {type: 'application/octet-stream'});
        const saveFileWithName = fileName ? fileName : `${name}${extension}`;
        saveAs(blob, saveFileWithName);
      }),
      take(1)
    ).subscribe({
      next: (_) => {
        if (mergedOptions.showSnackbar) {
          this.sharedService.openSnackBar(mergedOptions.successMessage);
        }
        else if (mergedOptions.successCallback) {
          mergedOptions.successCallback();
        }
      },
      error: (error) => {
        if (mergedOptions.showSnackbar) {
          this.sharedService.openSnackBar(mergedOptions.errorMessage);
        }
        else if (mergedOptions.errorCallback) {
          mergedOptions.errorCallback(error);
        }
      }
    });
  }

  base64toBlob(base64Data, contentType) {
    contentType = contentType || '';
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);

      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, {type: contentType});
  }
}
